import { LayoutConfig, StoreUIMenu } from '@insite-store360/shared';

const ALWAYS_ALLOWED_PREFIXES = [
  '/privacypolicy',
] as const;

const PM_SHARED_ALLOWED_PREFIXES = [
  '/admin/user/users',
  '/admin/user/master-view',
  '/admin/user/tds-view',
  '/admin/store/user-store-mapping',
] as const;

const RELATED_ROUTE_PREFIXES: ReadonlyArray<{
  readonly whenAllowed: string;
  readonly alsoAllow: readonly string[];
}> = [
  {
    whenAllowed: '/admin/project-management/leads-summary',
    alsoAllow: [
      '/admin/project-management/create-lead',
      '/admin/project-management/approval-request',
      '/admin/project-management/cost-preparation',
    ],
  },
  {
    whenAllowed: '/admin/project-management/create-lead',
    alsoAllow: [
      '/admin/project-management/approval-request',
      '/admin/project-management/cost-preparation',
    ],
  },
  {
    whenAllowed: '/admin/project-management/adjust-awarded-leads',
    alsoAllow: ['/admin/project-management/adjust-awarded-leads/edit-awarded'],
  },
  {
    whenAllowed: '/admin/store360',
    alsoAllow: [
      '/admin/store/stores-dev',
      '/admin/store360/account',
      '/admin/store/stores-dev/account',
    ],
  },
  {
    whenAllowed: '/admin/store/stores-dev',
    alsoAllow: [
      '/admin/store360',
      '/admin/store360/account',
      '/admin/store/stores-dev/account',
    ],
  },
  {
    whenAllowed: '/admin/user/display-planogram',
    alsoAllow: ['/admin/user/display-planogram-export'],
  },
  {
    whenAllowed: '/admin/survey/displaymatrix',
    alsoAllow: ['/admin/survey/displaymatrix-export'],
  },
  {
    whenAllowed: '/admin/survey/display-compliance',
    alsoAllow: ['/admin/survey/display-compliance-export'],
  },
];

function normalizePath(url: string): string {
  const path = (url || '').split('?')[0].split('#')[0].trim();
  if (!path) {
    return '/';
  }
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
}

function isInternalAppPath(routerLink: string): boolean {
  if (!routerLink || typeof routerLink !== 'string') {
    return false;
  }
  const trimmed = routerLink.trim();
  if (!trimmed.startsWith('/')) {
    return false;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return false;
  }
  return true;
}

function collectFromMenu(menu: StoreUIMenu[] | undefined, into: Set<string>): void {
  if (!menu?.length) {
    return;
  }

  for (const item of menu) {
    if (item.visible === false) {
      continue;
    }

    if (isInternalAppPath(item.routerLink)) {
      into.add(normalizePath(item.routerLink));
    }

    if (item.subMenu?.length) {
      collectFromMenu(item.subMenu, into);
    }
  }
}

export function collectAllowedRoutesFromLayout(layoutConfig: LayoutConfig | undefined | null): string[] {
  const allowed = new Set<string>();
  if (!layoutConfig) {
    return [];
  }

  collectFromMenu(layoutConfig.sideNavConfig?.menu, allowed);
  collectFromMenu(layoutConfig.menu, allowed);

  for (const rule of RELATED_ROUTE_PREFIXES) {
    if ([...allowed].some((path) => pathMatchesPrefix(path, rule.whenAllowed))) {
      for (const related of rule.alsoAllow) {
        allowed.add(normalizePath(related));
      }
    }
  }

  return [...allowed];
}

export function pathMatchesPrefix(path: string, prefix: string): boolean {
  const normalizedPath = normalizePath(path);
  const normalizedPrefix = normalizePath(prefix);
  return (
    normalizedPath === normalizedPrefix ||
    normalizedPath.startsWith(`${normalizedPrefix}/`)
  );
}

export function isPathAllowedByRoutes(
  url: string,
  allowedRoutes: readonly string[],
  options?: {
    readonly allowPmSharedRoutes?: boolean;
    readonly extraAllowedPrefixes?: readonly string[];
  },
): boolean {
  const path = normalizePath(url);

  if (ALWAYS_ALLOWED_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix))) {
    return true;
  }

  if (options?.extraAllowedPrefixes?.some((prefix) => pathMatchesPrefix(path, prefix))) {
    return true;
  }

  if (
    options?.allowPmSharedRoutes &&
    PM_SHARED_ALLOWED_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix))
  ) {
    return true;
  }

  return allowedRoutes.some((route) => pathMatchesPrefix(path, route));
}

export function resolveFallbackRoute(
  allowedRoutes: readonly string[],
  preferredRoutes: readonly string[],
): string {
  for (const preferred of preferredRoutes) {
    if (allowedRoutes.some((route) => pathMatchesPrefix(route, preferred) || pathMatchesPrefix(preferred, route))) {
      return preferred;
    }
  }

  const firstAdminRoute = allowedRoutes.find((route) => route.startsWith('/admin/'));
  return firstAdminRoute || preferredRoutes[0] || '/admin/landing-page';
}
